import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import JoditEditor from 'jodit-react';

// Helper function to check if HTML content is empty (only tags, no actual text)
const isHtmlEmpty = (html) => {
  if (!html) return true;
  // Remove all HTML tags
  let stripped = html.replace(/<[^>]*>/g, '');
  // Remove HTML entities like &nbsp;
  stripped = stripped.replace(/&nbsp;/g, ' ');
  // Remove all whitespace and check if anything remains
  stripped = stripped.trim();
  return stripped.length === 0;
};

const joditConfig = {
  readonly: false,
  height: 220,
  toolbarButtonSize: 'small',
  buttons: [
    'bold', 'italic', 'underline', '|',
    'ul', 'ol', '|',
    'link', '|',
    'paragraph', '|',
    'undo', 'redo',
  ],
  removeButtons: ['image', 'video', 'file', 'speechRecognize', 'spellcheck', 'about', 'fullsize', 'print'],
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',
  style: { fontSize: '14px' },
};

const AddActionModal = ({ visible, onClose, onSubmit, editData, onDelete }) => {
  const editorRef = useRef(null);
  const [actionType, setActionType] = useState('');
  const [title, setTitle] = useState('');
  const [titleBackgroundColor, setTitleBackgroundColor] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [subTitleBackgroundColor, setSubTitleBackgroundColor] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionBackgroundColor, setDescriptionBackgroundColor] = useState('');
  const [subDescription, setSubDescription] = useState('');
  const [subDescriptionBackgroundColor, setSubDescriptionBackgroundColor] = useState('');
  const [defaultMessage, setDefaultMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    if (visible) {
      setErrors({});
      setActionType(isEdit ? (editData.actionType || '') : '');
      setTitle(isEdit ? (editData.title || '') : '');
      setTitleBackgroundColor(isEdit ? (editData.titleBackgroundColor || '') : '');
      setSubTitle(isEdit ? (editData.subTitle || '') : '');
      setSubTitleBackgroundColor(isEdit ? (editData.subTitleBackgroundColor || '') : '');
      setDescription(isEdit ? (editData.description || '') : '');
      setDescriptionBackgroundColor(isEdit ? (editData.descriptionBackgroundColor || '') : '');
      setSubDescription(isEdit ? (editData.subDescription || '') : '');
      setSubDescriptionBackgroundColor(isEdit ? (editData.subDescriptionBackgroundColor || '') : '');
      if (isEdit && editData.actionConfig) {
        try {
          const cfg = typeof editData.actionConfig === 'string'
            ? JSON.parse(editData.actionConfig)
            : editData.actionConfig;
          setDefaultMessage(cfg.defaultMessage || '');
        } catch {
          setDefaultMessage('');
        }
      } else {
        setDefaultMessage('');
      }
    }
  }, [visible, editData]);

  const handleSubmit = async () => {
    const errs = {};
    if (!actionType.trim()) errs.actionType = 'Action type is required.';
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }

    setErrors({});
    setLoading(true);
    try {
      const actionConfig = JSON.stringify({
        template: 'default',
        sendEmail: true,
        sendSMS: false,
        allowCustomMessage: true,
        defaultMessage: isHtmlEmpty(defaultMessage) ? null : defaultMessage,
      });
      await onSubmit({
        actionType: actionType.trim(),
        title: title.trim() || null,
        titleBackgroundColor: titleBackgroundColor.trim() || null,
        subTitle: subTitle.trim() || null,
        subTitleBackgroundColor: subTitleBackgroundColor.trim() || null,
        description: description.trim() || null,
        descriptionBackgroundColor: descriptionBackgroundColor.trim() || null,
        subDescription: isHtmlEmpty(subDescription) ? null : subDescription,
        subDescriptionBackgroundColor: subDescriptionBackgroundColor.trim() || null,
        actionConfig,
      });
    } catch {
      setErrors({ defaultMessage: 'Failed to save. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>{isEdit ? 'Edit Action' : 'Add Action'}</span>}
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="ag-modal"
      width={680}
      bodyStyle={{ padding: '20px 28px 28px', borderTop: "1px solid #bfbfbf", maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {/* Action Type */}
      <div style={{ marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>
          Action Type <span style={{ color: '#720C20' }}>*</span>
        </label>
      </div>
      <Input
        placeholder="e.g., Notify Agent"
        value={actionType}
        onChange={e => { setActionType(e.target.value); if (errors.actionType) setErrors(p => ({ ...p, actionType: '' })); }}
        style={{ borderRadius: 2, borderColor: errors.actionType ? '#ff4d4f' : '#720C20', height: 40, marginBottom: 4 }}
      />
      {errors.actionType && (
        <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 0, marginBottom: 12 }}>{errors.actionType}</p>
      )}

      {/* Title & BG Color */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Title <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={titleBackgroundColor} onChange={e => setTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Sub Title & BG Color */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Sub Title <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          <Input placeholder="Sub title" value={subTitle} onChange={e => setSubTitle(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={subTitleBackgroundColor} onChange={e => setSubTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Description & BG Color */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Description <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <Input.TextArea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ flex: 2, borderRadius: 2 }} />
          <Input placeholder="#ffffff" value={descriptionBackgroundColor} onChange={e => setDescriptionBackgroundColor(e.target.value)} style={{ flex: 1, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Sub Description & BG Color */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Sub Description <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <div style={{ flex: 2, border: '1px solid #d9d9d9', borderRadius: 2, overflow: 'hidden' }}>
            <JoditEditor
              value={subDescription}
              config={joditConfig}
              onBlur={val => setSubDescription(val)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input placeholder="#ffffff" value={subDescriptionBackgroundColor} onChange={e => setSubDescriptionBackgroundColor(e.target.value)} style={{ borderRadius: 2, height: 38 }} />
          </div>
        </div>
      </div>

      {/* Default Message — rich text editor */}
      <div style={{ marginBottom: 6, marginTop: 14 }}>
        <label style={{ fontSize: 13, color: '#333' }}>Default Message</label>
      </div>
      <div style={{ border: errors.defaultMessage ? '1px solid #ff4d4f' : '1px solid #720C20', borderRadius: 2, overflow: 'hidden' }}>
        <JoditEditor
          ref={editorRef}
          value={defaultMessage}
          config={joditConfig}
          onBlur={val => { setDefaultMessage(val); if (errors.defaultMessage) setErrors(p => ({ ...p, defaultMessage: '' })); }}
        />
      </div>
      {errors.defaultMessage && (
        <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{errors.defaultMessage}</p>
      )}

      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 24 }}>
        {isEdit && onDelete && (
          <Button
            danger
            icon={<DeleteOutlined color='rgb(114, 12, 32)' />}
            onClick={() => setConfirmVisible(true)}
            disabled={loading}
            style={{ borderRadius: 2, fontWeight: 500, display: 'flex', alignItems: 'center', borderColor: "rgb(114, 12, 32)", color: "rgb(114, 12, 32)" }}
          >
            Delete
          </Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button onClick={onClose} disabled={loading} style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
          <Button loading={loading} onClick={handleSubmit} style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 2, fontWeight: 500 }}>
            {isEdit ? 'Update Action' : 'Add Action'}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.actionType}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.actionId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddActionModal;
